/* Gertab-compatible SKU handoff. Shopify remains the source of cart quantities. */
(() => {
  'use strict';
  if (window.lbvExternalCheckoutReady) return;
  window.lbvExternalCheckoutReady = true;
  const pending = new Map();

  const checkoutURL = form => {
    const endpoint = form.dataset.checkoutUrl.trim();
    const url = new URL(endpoint);
    if (!endpoint.startsWith('https://') || /[\s\\]/.test(endpoint) ||
        url.protocol !== 'https:' || !url.hostname.includes('.') ||
        url.username || url.password || url.hash ||
        url.searchParams.has('av_cart') || url.searchParams.has('mvlang')) {
      throw new Error(form.dataset.checkoutUnavailable);
    }
    return url;
  };

  const requestCart = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options,
        headers: {Accept: 'application/json', ...options.headers}, signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      return cart;
    } finally {
      clearTimeout(timeout);
    }
  };

  document.addEventListener('submit', event => {
    if (pending.has(event.target)) event.preventDefault();
  }, true);

  document.addEventListener('click', async event => {
    const form = event.target.closest('[data-external-checkout-form]');
    if (!form) return;
    if (pending.has(form)) { event.preventDefault(); return; }
    const link = event.target.closest('[data-external-checkout]');
    if (!link) return;
    event.preventDefault();
    if (form.dataset.submitting === 'true' || !form.reportValidity()) return;

    const status = form.querySelector('[data-checkout-status]');
    const inputs = [...form.querySelectorAll('[data-cart-line-key]')];
    const edits = inputs.filter(input => input.value !== input.defaultValue);
    const note = form.querySelector('[data-cart-note]');
    const noteChanged = Boolean(note && note.value !== note.defaultValue);
    const controls = [...form.querySelectorAll('input, button, textarea')].filter(control => !control.disabled);
    const restore = () => {
      controls.forEach(control => { control.disabled = false; });
      form.removeAttribute('aria-busy');
      link.removeAttribute('aria-disabled');
      pending.delete(form);
    };
    pending.set(form, restore);
    controls.forEach(control => { control.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');
    status.hidden = true;
    let leaving = false;
    let message = form.dataset.checkoutError;

    try {
      let url;
      try { url = checkoutURL(form); }
      catch { message = form.dataset.checkoutUnavailable; throw new Error(message); }

      let cart = await requestCart(form.dataset.cartJsonUrl);
      const updates = {};
      for (const input of edits) {
        const line = cart.items.find(item => item.key === input.dataset.cartLineKey);
        const quantity = Number(input.value);
        if (!line || !Number.isSafeInteger(quantity) || quantity < 0) {
          message = form.dataset.checkoutCartChanged;
          throw new Error(message);
        }
        if (line.quantity !== quantity) updates[line.key] = quantity;
      }
      if (Object.keys(updates).length || noteChanged) {
        cart = await requestCart(form.dataset.cartUpdateUrl, {
          method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({updates, ...(noteChanged ? {note: note.value} : {})})
        });
      }
      if (!cart.items.length) { message = form.dataset.checkoutEmpty; throw new Error(message); }
      const pieces = cart.items.map(item => {
        const sku = String(item.sku || '').trim();
        if (!sku || /[:,]/.test(sku) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) {
          message = form.dataset.checkoutMissingSku;
          throw new Error(message);
        }
        return [sku, item.quantity].join(':');
      });
      url.searchParams.set('av_cart', pieces.join(','));
      const language = form.dataset.checkoutLanguage.trim();
      if (language) url.searchParams.set('mvlang', language);
      // A section may have been removed in the theme editor during the request.
      if (!form.isConnected) return;
      window.location.assign(url.href);
      leaving = true;
    } catch {
      status.textContent = message;
      status.hidden = false;
      status.focus();
    } finally {
      if (!leaving) restore();
    }
  });

  // Restore controls when returning from checkout via the browser's back cache.
  window.addEventListener('pageshow', () => [...pending.values()].forEach(restore => restore()));
})();
